import React, { useState, useEffect } from 'react';
import { Search, User, Mail, BookOpen, Plus, Edit2, Trash2, Eye, Phone, MapPin, UserCheck, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

// Composant pour champ de mot de passe avec icône œil
const PasswordInput = ({ label, name, value, onChange, placeholder, required, error, icon: Icon }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-4 w-4 text-gray-400" />
          </div>
        )}
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={`block w-full ${Icon ? 'pl-10' : 'pl-3'} pr-10 py-2 text-sm border ${
            error ? 'border-red-300' : 'border-gray-300'
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
      {error && (
        <p className="text-red-600 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

// Composant pour select multiple style Select2
const Select2StyleMultiSelect = ({ label, name, value, onChange, options, required, error, help }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const currentValue = Array.isArray(value) ? value : [];
  const selectedOptions = options.filter(option => currentValue.includes(option.value));
  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) && 
    !currentValue.includes(option.value)
  );

  const handleSelect = (optionValue) => {
    const currentValue = Array.isArray(value) ? value : [];
    const newValue = [...currentValue, optionValue];
    onChange({ target: { name, value: newValue } });
    setSearchTerm('');
    // Garder le dropdown ouvert pour permettre d'autres sélections
  };

  const handleRemove = (optionValue) => {
    const currentValue = Array.isArray(value) ? value : [];
    const newValue = currentValue.filter(v => v !== optionValue);
    onChange({ target: { name, value: newValue } });
  };

  const handleKeyDown = (e) => {
    const currentValue = Array.isArray(value) ? value : [];
    if (e.key === 'Backspace' && searchTerm === '' && currentValue.length > 0) {
      handleRemove(currentValue[currentValue.length - 1]);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <div 
          className={`min-h-[42px] w-full px-3 py-2 text-sm border ${
            error ? 'border-red-300' : isOpen ? 'border-blue-500' : 'border-gray-300'
          } rounded-md bg-white cursor-text hover:border-gray-400 transition-colors ${
            isOpen ? 'ring-1 ring-blue-500' : ''
          }`}
          onClick={() => setIsOpen(true)}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selectedOptions.map(option => (
              <span
                key={option.value}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {option.label}
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemove(option.value);
                  }}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 focus:outline-none"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsOpen(true)}
              onBlur={(e) => {
                // Fermer seulement si on ne clique pas sur le dropdown
                setTimeout(() => {
                  setIsOpen(false);
                }, 200);
              }}
              placeholder={selectedOptions.length === 0 ? "Rechercher et sélectionner..." : ""}
              className="flex-1 min-w-[120px] bg-transparent text-sm"
              style={{ 
                outline: 'none', 
                border: 'none', 
                boxShadow: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none'
              }}
            />
          </div>
        </div>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            <div className="select-dropdown absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <div
                    key={option.value}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Empêche le blur de l'input
                      handleSelect(option.value);
                    }}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-900 select-none"
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchTerm ? 'Aucun résultat trouvé' : 'Toutes les options sont sélectionnées'}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {help && (
        <p className="text-xs text-gray-500 mt-1">{help}</p>
      )}
      {error && (
        <p className="text-red-600 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

const CorrecteursTab = () => {
  const [correcteurs, setCorrecteurs] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCorrecteur, setSelectedCorrecteur] = useState(null);
  const [detailCorrecteur, setDetailCorrecteur] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    adresse: '',
    specialites: []
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [correcteurToDelete, setCorrecteurToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchCorrecteurs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/correcteurs/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setCorrecteurs(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des correcteurs');
      setCorrecteurs([]);
      setPagination(null);
      toast.error('Impossible de charger les correcteurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatieres = async () => {
    try {
      const response = await api.get('/concours/matieres/');
      const responseData = response.data.data || response.data.results || [];
      setMatieres(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
      setMatieres([]);
    }
  };

  useEffect(() => {
    fetchMatieres();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCorrecteurs();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'specialites') {
      // Pour le composant Select2StyleMultiSelect, la valeur est déjà un tableau
      setFormData(prev => ({
        ...prev,
        [name]: Array.isArray(value) ? value : []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (correcteur = null) => {
    if (correcteur) {
      setSelectedCorrecteur(correcteur);
      setFormData({
        username: correcteur.utilisateur?.username || '',
        email: correcteur.utilisateur?.email || '',
        first_name: correcteur.utilisateur?.first_name || '',
        last_name: correcteur.utilisateur?.last_name || '',
        password: '', // Ne pas pré-remplir le mot de passe
        confirmPassword: '',
        telephone: correcteur.telephone || '',
        adresse: correcteur.adresse || '',
        specialites: correcteur.specialites?.map(s => s.id) || []
      });
    } else {
      setSelectedCorrecteur(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirmPassword: '',
        telephone: '',
        adresse: '',
        specialites: []
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (correcteur) => {
    setDetailCorrecteur(correcteur);
    setIsDetailModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!selectedCorrecteur) {
      // Validation pour création
      if (!formData.username.trim()) {
        errors.username = "Le nom d'utilisateur est requis";
      }
      if (!formData.email.trim()) {
        errors.email = "L'email est requis";
      }
      if (!formData.password.trim()) {
        errors.password = "Le mot de passe est requis";
      } else if (formData.password.length < 8) {
        errors.password = "Le mot de passe doit contenir au moins 8 caractères";
      }
      if (!formData.confirmPassword.trim()) {
        errors.confirmPassword = "La confirmation du mot de passe est requise";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }
    
    if (!formData.first_name.trim()) {
      errors.first_name = "Le prénom est requis";
    }
    if (!formData.last_name.trim()) {
      errors.last_name = "Le nom est requis";
    }
    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'email n'est pas valide";
    }
    if (formData.specialites.length === 0) {
      errors.specialites = "Au moins une spécialité est requise";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (selectedCorrecteur) {
        // Mise à jour (seules certaines données peuvent être modifiées)
        const dataToSend = {
          telephone: formData.telephone,
          adresse: formData.adresse,
          specialites: formData.specialites
        };
        
        await api.put(`/concours/correcteurs/${selectedCorrecteur.id}/`, dataToSend);
        toast.success("Correcteur mis à jour avec succès");
      } else {
        // Création
        await api.post('/concours/correcteurs/', formData);
        toast.success("Correcteur créé avec succès");
      }

      fetchCorrecteurs();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (correcteur) => {
    setCorrecteurToDelete(correcteur);
    setConfirmConfig({
      title: "Supprimer le correcteur",
      message: `Êtes-vous sûr de vouloir supprimer le correcteur "${correcteur.utilisateur?.first_name} ${correcteur.utilisateur?.last_name}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!correcteurToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/correcteurs/${correcteurToDelete.id}/`);
      toast.success("Correcteur supprimé avec succès");
      fetchCorrecteurs();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer ce correcteur. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  const getStatutBadge = (statut) => {
    const statusConfig = {
      'ACTIF': { color: 'bg-green-100 text-green-800', icon: UserCheck, label: 'Actif' },
      'INACTIF': { color: 'bg-gray-100 text-gray-800', icon: User, label: 'Inactif' },
      'SUSPENDU': { color: 'bg-red-100 text-red-800', icon: User, label: 'Suspendu' }
    };
    
    const config = statusConfig[statut] || { color: 'bg-gray-100 text-gray-800', icon: User, label: statut };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedCorrecteur ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-end">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && correcteurs.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher un correcteur..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouveau Correcteur
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {correcteurs.length === 0 && !loading ? (
        <EmptyState
          icon={User}
          title="Aucun correcteur"
          description="Aucun correcteur trouvé."
          actionLabel="Créer un correcteur"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correcteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Spécialités
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {correcteurs.map((correcteur) => (
                  <tr key={correcteur.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {correcteur.utilisateur?.first_name} {correcteur.utilisateur?.last_name}
                          </div>
                          <div className="text-sm text-gray-500">@{correcteur.utilisateur?.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <Mail className="h-4 w-4 mr-1 text-gray-400" />
                          {correcteur.utilisateur?.email}
                        </div>
                        {correcteur.telephone && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Phone className="h-4 w-4 mr-1 text-gray-400" />
                            {correcteur.telephone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <BookOpen className="h-4 w-4 mr-1 text-gray-400" />
                        {correcteur.specialites?.length || 0} spécialité(s)
                      </div>
                      {correcteur.specialites?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {correcteur.specialites.slice(0, 2).map(spec => (
                            <span key={spec.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {spec.code}
                            </span>
                          ))}
                          {correcteur.specialites.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              +{correcteur.specialites.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatutBadge(correcteur.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenDetailModal(correcteur)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(correcteur)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(correcteur)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCorrecteur ? 'Modifier le correcteur' : 'Nouveau correcteur'}
        footer={renderModalFooter()}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {!selectedCorrecteur && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Informations de connexion</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Nom d'utilisateur"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Ex: amadou.diallo"
                  required
                  error={formErrors.username}
                  icon={User}
                />
                
                <div></div> {/* Spacer */}
                
                <PasswordInput
                  label="Mot de passe"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 8 caractères"
                  required
                  error={formErrors.password}
                  icon={Eye}
                />

                <PasswordInput
                  label="Confirmer le mot de passe"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirmer votre mot de passe"
                  required
                  error={formErrors.confirmPassword}
                  icon={Eye}
                />
              </div>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Informations personnelles</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Prénom"
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Ex: Amadou"
                required
                error={formErrors.first_name}
                icon={User}
              />
              
              <FormInput
                label="Nom"
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Ex: Diallo"
                required
                error={formErrors.last_name}
                icon={User}
              />

              <FormInput
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Ex: amadou.diallo@email.com"
                required
                error={formErrors.email}
                icon={Mail}
              />

              <FormInput
                label="Téléphone"
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                placeholder="Ex: 96 12 34 56"
                error={formErrors.telephone}
                icon={Phone}
              />
            </div>

            <div className="mt-4">
              <FormInput
                label="Adresse"
                type="textarea"
                name="adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                placeholder="Adresse complète du correcteur..."
                error={formErrors.adresse}
                rows={3}
                icon={MapPin}
              />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-green-900 mb-3">Spécialités</h4>
            <Select2StyleMultiSelect
              label="Matières de spécialité"
              name="specialites"
              value={formData.specialites}
              onChange={handleInputChange}
              options={matieres.map(matiere => ({ 
                value: matiere.id, 
                label: `${matiere.code} - ${matiere.nom}` 
              }))}
              required
              error={formErrors.specialites}
              help="Recherchez et sélectionnez les matières. Cliquez sur × pour retirer une matière."
            />
          </div>

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails du correcteur"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-2xl"
      >
        {detailCorrecteur && (
          <div className="space-y-6">
            {/* Informations personnelles */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-green-600" />
                Informations personnelles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom complet</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailCorrecteur.utilisateur?.first_name} {detailCorrecteur.utilisateur?.last_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom d'utilisateur</label>
                  <p className="mt-1 text-sm text-gray-900">@{detailCorrecteur.utilisateur?.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{detailCorrecteur.utilisateur?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                  <p className="mt-1 text-sm text-gray-900">{detailCorrecteur.telephone || 'Non renseigné'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Adresse</label>
                  <p className="mt-1 text-sm text-gray-900">{detailCorrecteur.adresse || 'Non renseignée'}</p>
                </div>
              </div>
            </div>

            {/* Spécialités */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                Spécialités ({detailCorrecteur.specialites?.length || 0})
              </h3>
              {detailCorrecteur.specialites?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {detailCorrecteur.specialites.map(spec => (
                    <div key={spec.id} className="bg-white p-3 rounded-lg border">
                      <div className="font-medium text-gray-900">{spec.nom}</div>
                      <div className="text-sm text-gray-500">Code: {spec.code}</div>
                      {spec.description && (
                        <div className="text-sm text-gray-600 mt-1">{spec.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Aucune spécialité assignée</p>
              )}
            </div>

            {/* Statut et statistiques */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statut et activité</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statut</label>
                  <div className="mt-1">{getStatutBadge(detailCorrecteur.statut)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Attributions en cours</label>
                  <p className="mt-1 text-sm text-gray-900">{detailCorrecteur.nombre_attributions || 0}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date de création</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailCorrecteur.date_creation ? 
                      new Date(detailCorrecteur.date_creation).toLocaleDateString('fr-FR') : 
                      'Non disponible'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le correcteur"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce correcteur ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default CorrecteursTab;